<template>
  <section>
    <one-line-form :submit="updateStreetName" :submittable="isValid" :button="showStreets === null" :saving="isSaving ? streetName : ''" :loading="true">
      <input-field name="street"
                   :class="{ invisible: isSaving || !openAnswer }"
                   type="text"
                   :value="streetName"
                   :suggestion="matchStreetName"
                   @maskInput="syncStreetName"
                   :title="i18n.t('street:street_name')"
                   :placeholder="i18n.t('street:enter_street_name')"
                   :autocapitalize="true"
                   v-mask="partner.inputFieldsRegExp ? partner.inputFieldsRegExp : null" />
    </one-line-form>
    <div v-if="!openAnswer && visibleQuestion" :button="false" class="question">
      <h5>{{ i18n.t('street:select_street') }}</h5>
    </div>
    <helper v-if="entity.country === 'US'" :class="{ invisible : isSaving, actionhint: showStreets }">
      {{ entity.city }}{{ entity.province && `, ${entity.province }` }}{{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : `, ${entity.postalCode}`}}
    </helper>
    <helper v-else :class="{ invisible : isSaving, actionhint: showStreets }">
      {{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : entity.postalCode}} {{ entity.city }}
    </helper>
    <numb-area></numb-area>

    <search-selector
      v-if="showStreets"
      :options="streets"
      @submit="selectStreet"
      :has-other="true"
      :placeholder="i18n.t('street:search_street_name')"
      @other="selectStreet()"
    />

    <show-keyboard v-if="!streets || showStreets === null"></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  @include desktop-big {
    section {
      .actions {
        overflow: visible !important;
        margin-left: 0 !important;
        left: 0;

        .scroll-shadow {
          .shadow {
            .bottom {
              bottom: -22px !important;
            }
          }
        }
      }

      ul {
        margin-top: 55px;
        max-height: none !important;
        overflow: visible !important;

        li {
          font-weight: normal !important;
        }
      }
    }
  }

  ul {
    @include button-list;
  }
  .actionhint {
    pointer-events: none;
  }
  form .invisible{
    position: absolute;
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'

  import { setSelection } from '../utils/selection'
  import BottomHints from '../components/BottomHints'
  import { getNestedProperty, areConditionsPassed } from '../utils/structure.js'
  import searchSelector from 'components/SearchSelector'

  export default Vue.component('personal-street-name-screen', {
    components: {
      'bottom-hints': BottomHints,
      'search-selector': searchSelector
    },
    props: ['screenParams'],
    data () {
      const autofill = this.$store.state.autofill
      const postalCodePrediction = this.$store.state.postalCodePrediction
      const companyPrediction = this.$store.state.companyPrediction && this.$store.state.companyPrediction.matchCompany

      const dispatchKeyConditions = this.screenParams && this.screenParams.dispatchKey && this.screenParams.dispatchKey.conditions
      const dispatchKeyString = this.screenParams && this.screenParams.dispatchKey && typeof this.screenParams.dispatchKey === 'string' && this.screenParams.dispatchKey
      const dispatchKey = (dispatchKeyConditions && areConditionsPassed(this.$store, this.screenParams.dispatchKey.conditions) && this.screenParams.dispatchKey.prop) || dispatchKeyString || 'updateUser'
      const key = dispatchKey.replace('update', '').toLowerCase()
      const propertyName = (this.screenParams && this.screenParams.propertyName) || 'streetName'

      let propertyEntity = propertyName.split('.')
      propertyEntity = propertyEntity.length > 1 ? propertyEntity.slice(0, -1).join('.') : false
      const entityData = propertyEntity ? getNestedProperty(this.$store.state[key], propertyEntity) : this.$store.state[key]

      const userStreetPrediction = postalCodePrediction.enabled && postalCodePrediction.matchPerson.streetName
      const companyStreetPrediction = companyPrediction && companyPrediction.streetName
      const allowedCountriesForReverseLookup = ['US', 'DE', 'SE', 'GB', 'NO', 'FI', 'DK', 'SG', 'CA', 'RU', 'UA', 'TR', 'ID', 'MY', 'KZ', 'IL']
      const canDoPersonalDataLookup = this.$store.getters.canDoPersonalDataLookup

      return {
        streetName: entityData.streetName || (key === 'user' && userStreetPrediction) || (key === 'company' && companyStreetPrediction) || (autofill.enabled && autofill.streetName) || '',
        matchStreetName: '',
        showStreets: false,
        isSaving: false,
        visibleQuestion: true,
        entity: entityData,
        propertyName: propertyName,
        dispatchKey: dispatchKey,
        willLookupPostalCode: entityData.postalCode === this.$store.state.app.unknownPostcode && allowedCountriesForReverseLookup.includes(entityData.country) && canDoPersonalDataLookup
      }
    },
    computed: {
      ...mapState(['user', 'postalArea', 'app', 'partner']),

      streets () {
        return (this.postalArea.streets || []).sort(this.sortAlphebetically)
      },
      openAnswer () {
        return this.showStreets === null || !this.streets.length
      },
      isValid () {
        return !!this.streetName
      }
    },
    watch: {
      streetName (newVal, oldVal) {
        if (newVal.length > 0) {
          let matchStreetName = this.streets.find((t) => t.value.substring(0, newVal.length).toLowerCase() === newVal.toLowerCase())
          if (matchStreetName) {
            this.matchStreetName = matchStreetName.value
            this.streetName = `${newVal.charAt(0).toUpperCase()}${newVal.slice(1)}`
          } else {
            this.matchStreetName = ''
          }
        } else {
          this.matchStreetName = ''
        }
      },
      isValid () {
        if (this.isValid && this.showStreets) {
          this.$emit('tooltip', ['vertical', 'enter'])
        } else if (this.isValid) {
          this.$emit('tooltip', ['enter'])
        } else if (!this.showStreets) {
          this.$emit('tooltip', ['type'])
        }
      },
      isSaving () {
        if (this.isSaving) {
          this.$emit('tooltip', 'hide')
        }
      }
    },
    mounted () {
      if (this.streetName === '' && this.postalArea.streets && this.entity.postalCode !== this.app.unknownPostcode) {
        this.showStreets = true
        this.$emit('tooltip', ['vertical', 'enter'])
      } else {
        this.selectStreet()
        this.$emit('tooltip', ['type'])
      }
    },
    methods: {
      selectStreet (street) {
        this.visibleQuestion = false

        if (street && street.value) {
          this.streetName = street.value

          if (street.postalCode) {
            let dispatchData = {}
            const property = this.propertyName.split('.')
            const propertyPostalcode = property.length > 1 ? property.slice(0, -1).join('.') + '.postalCode' : 'postalCode'
            const propertyProvince = property.length > 1 ? property.slice(0, -1).join('.') + '.province' : 'province'

            dispatchData[propertyPostalcode] = street.postalCode
            dispatchData[propertyProvince] = street.province

            this.$store.dispatch(this.dispatchKey, dispatchData)

            this.willLookupPostalCode = false
          }
          this.updateStreetName()
        }

        if (!street && this.showStreets !== null) {
          setTimeout(() => {
            this.showStreets = null
          }, 300)
        }

        if (!street && this.willLookupPostalCode && this.streets.length > 1) {
          this.willLookupPostalCode = false
          this.updateStreetName()
        }

        this.$emit('tooltip', ['type'])

        const input = this.$el.querySelector('input')

        if (input) {
          if (this.isSaving) input.setAttribute('readonly', 'true')
          const length = this.streetName ? this.streetName.length : 0
          setSelection(input, { start: length, end: length })
        }
      },
      syncStreetName (value) {
        if (this.usingPrefill && value !== this.streetName) this.usingPrefill = false
        this.streetName = value === '' ? this.streetName : value
      },
      async updateStreetName () {
        if (this.streetName && !this.willLookupPostalCode) {
          if (this.matchStreetName) {
            this.streetName = this.matchStreetName
          }
          this.isSaving = true

          let dispatchData = {}
          dispatchData[this.propertyName] = this.streetName
          this.$store.dispatch(this.dispatchKey, dispatchData)

          this.trackPrefill('streetName', this.streetName)

          setTimeout(() => {
            this.showStreets = false
            this.$store.dispatch('next', this.screenParams.nextScreenRoute)
          }, 300)
        } else {
          this.isSaving = true
          await this.$store.dispatch('lookupPostalCode', { province: this.entity.province, city: this.entity.city, streetName: this.streetName, houseNumber: '' })
          if (this.postalArea.streets.length === 0) {
            this.willLookupPostalCode = false
            return this.updateStreetName()
          } else if (this.postalArea.streets.length === 1) {
            let street = this.postalArea.streets[0]

            this.streetName = street.value

            let dispatchData = {}
            const property = this.propertyName.split('.')
            const propertyPostalcode = property.length > 1 ? property.slice(0, -1).join('.') + '.postalCode' : 'postalCode'
            const propertyProvince = property.length > 1 ? property.slice(0, -1).join('.') + '.province' : 'province'

            dispatchData[propertyPostalcode] = street.postalCode
            if (street.province) {
              dispatchData[propertyProvince] = street.province
            }

            this.$store.dispatch(this.dispatchKey, dispatchData)

            this.willLookupPostalCode = false
            return this.updateStreetName()
          }
          this.showStreets = true
          this.isSaving = false
          this.visibleQuestion = true
        }
      },
      sortAlphebetically (a, b) {
        if (a.value < b.value) return -1
        if (a.value > b.value) return 1
        return 0
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/PersonalStreetName.vue